import { useEffect, useRef } from 'react';
import { createScanReader } from '@/lib/scanReader';

/**
 * How long after a keystroke to check whether the burst has ended. Only ever
 * as long as the reader's own settle window, which is what actually decides;
 * this just has to be no shorter, or it wakes up before there is a verdict.
 */
const SETTLE_POLL_MS = 200;

/**
 * Listens for a "wedge" barcode scanner — the Zebra devices on the floor are
 * configured (see the DataWedge profile in the README) to deliver a scan as a
 * burst of keystrokes ending in ENTER, exactly as if someone had typed it very
 * fast. There is no scanner API to call: the payload simply arrives as keyboard
 * input, so the app listens globally and the operator never has to tap "Scan"
 * first — they pull the trigger and the pod opens.
 *
 * A scan completes one of two ways: on the ENTER suffix, or — when the device
 * profile sends no suffix — on the burst going quiet. The second path is not a
 * nicety. Without it a scanner configured without ENTER fills the buffer and
 * then strands it there, which looks to the operator exactly like the app
 * ignoring the trigger, with nothing on screen to say otherwise.
 *
 * The rule for telling a scan from typing lives in createScanReader, where it
 * is covered by tests. This is only the wiring.
 *
 * The handler is held in a ref so a caller passing an inline arrow does not
 * tear down and rebuild the listener on every render, which would drop a scan
 * that landed mid-burst.
 */
export function useHardwareScanner(enabled: boolean, onScan: (code: string) => void) {
  const handler = useRef(onScan);
  handler.current = onScan;

  useEffect(() => {
    if (!enabled) return;
    const reader = createScanReader();
    let settleTimer: number | undefined;

    /** Whether the focused element means these keystrokes are someone typing. */
    const isTyping = () => {
      const el = document.activeElement as HTMLElement | null;
      const isField =
        !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      // A field can opt out — the pod finder does, so pulling the trigger while
      // its search box has focus still counts as a scan instead of typing the
      // barcode into a box the operator then has to read back.
      const passthrough = !!el && el.dataset?.['scanPassthrough'] === 'true';
      return isField && !passthrough;
    };

    const deliver = (code: string) => {
      try {
        handler.current(code.trim());
      } catch {
        // A throwing handler must not kill the listener — the next scan should
        // still work.
      }
    };

    const onKey = (e: KeyboardEvent) => {
      const typing = isTyping();

      const code = reader.push({ key: e.key, at: Date.now(), typing });

      // Re-arm on every key: the burst is finished only once one of these
      // timers is allowed to run out.
      window.clearTimeout(settleTimer);
      if (!code) {
        settleTimer = window.setTimeout(() => {
          const settled = reader.settle({ at: Date.now(), typing: isTyping() });
          if (settled) deliver(settled.trim());
        }, SETTLE_POLL_MS);
        return;
      }

      // Only swallow the keystroke once it has actually completed a scan, so a
      // stray Tab still moves focus the way it should. Nothing can be swallowed
      // on the settle path — by then the characters have already been typed
      // wherever focus was — so a suffix-less scan into the finder's box leaves
      // text behind. Harmless: resolving the scan closes that sheet.
      e.preventDefault();
      deliver(code);
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(settleTimer);
      window.removeEventListener('keydown', onKey);
    };
  }, [enabled]);
}
